/**
 * Whether a touch landed inside something that scrolls sideways of its own accord,
 * anywhere between the target and the element handling the gesture.
 *
 * A horizontal drag gesture and a horizontal scroll are the same movement, so whichever
 * one is nearer the finger has to win — which is always the scroller, because scrolling
 * something is a direct manipulation of it and the gesture is a shortcut. Every native
 * list does this with a nested scroller.
 *
 * There is a lot of scrollable width in this app for a swipe to trip over: the financial
 * tables scroll horizontally by design, and Arc's markdown replies put an
 * `overflow-x: auto` box around every code block and wide table.
 *
 * Only a scroller with somewhere left to go counts. A code block whose content fits has
 * the overflow style and nothing to scroll, and stealing the gesture from it would mean
 * the swipe silently stopped working over half the messages in a channel.
 */
export const startsInHorizontalScroller = (
  target: EventTarget | null,
  boundary: HTMLElement,
): boolean => {
  let node = target instanceof Element ? target : null
  while (node && node !== boundary) {
    if (node.scrollWidth > node.clientWidth) {
      const { overflowX } = window.getComputedStyle(node)
      if (overflowX === 'auto' || overflowX === 'scroll') return true
    }
    node = node.parentElement
  }
  return false
}

/**
 * The strip along a screen edge that belongs to the back gesture. iOS reserves about this
 * much for its swipe-back and Android's gesture navigation 24dp by default. Any wider and it
 * takes drags meant for what sits there, such as the avatar column down a timeline's left side.
 */
export const BACK_GESTURE_EDGE_PX = 28

/**
 * Whether a touch began in the left or right edge strip, where the platform's back gesture
 * starts (Android's from either side). A gesture of our own that also answered there would
 * fire on the same swipe as going back.
 */
export const startsAtScreenEdge = (clientX: number): boolean =>
  clientX <= BACK_GESTURE_EDGE_PX || clientX >= window.innerWidth - BACK_GESTURE_EDGE_PX

/** A drag ends with a synthesised click on whatever it was released over; swallow that one. */
export const suppressNextClick = () => {
  const handler = (event: MouseEvent) => {
    event.stopPropagation()
    event.preventDefault()
  }
  document.addEventListener('click', handler, { capture: true, once: true })
  setTimeout(() => document.removeEventListener('click', handler, { capture: true }), 0)
}
